import { Form, Select } from 'antd';
import React, { ComponentProps, memo, useMemo, useState } from 'react';

import DefaultFormItem from 'components/composites/DefaultFormItem';
import FormItemCol from 'components/forms/FormItemCol';
import { FILTER_NULL } from 'store/modules/routerUtils/selectors';
import { SearchTypes } from 'store/modules/search/types';
import useSearch from 'utils/use-search';

type Props = {
  clearSiteOnChange?: boolean;
  includeNoEnterprise?: boolean;
  placeholder?: string;
  resetSearchOnInteraction?: boolean;
  rules?: ComponentProps<typeof DefaultFormItem>['rules'];
  selectId?: string;
};

const defaultRules: Props['rules'] = [
  { required: true, message: 'Please select an enterprise' }
];

function BackOfficeEnterpriseSelect({
  clearSiteOnChange,
  includeNoEnterprise,
  placeholder = 'Enterprise',
  resetSearchOnInteraction,
  rules = defaultRules,
  selectId
}: Props): JSX.Element {
  const form = Form.useFormInstance();
  const [enterpriseSearchTerm, setEnterpriseSearchTerm] = useState<string | undefined>();

  const enterpriseSearchResults = useSearch(
    SearchTypes.UserEnterpriseMembershipSearch,
    enterpriseSearchTerm
  );

  const enterpriseOptions = useMemo(
    () => [
      ...(includeNoEnterprise && !enterpriseSearchTerm
        ? [{ label: 'No Enterprise', value: FILTER_NULL }]
        : []),
      ...(enterpriseSearchResults || []).map(({ name, id }) => ({
        label: name,
        value: id.toString()
      }))
    ],
    [enterpriseSearchResults, enterpriseSearchTerm, includeNoEnterprise]
  );

  const clearSearchTerm = () => setEnterpriseSearchTerm(undefined);

  const handleEnterpriseChange = () => {
    if (clearSiteOnChange) {
      form.setFieldsValue({ siteId: undefined });
    }
    if (resetSearchOnInteraction) {
      clearSearchTerm();
    }
  };

  return (
    <FormItemCol>
      <DefaultFormItem name="enterpriseId" label="Enterprise" rules={rules}>
        <Select
          id={selectId}
          showSearch
          placeholder={placeholder}
          options={enterpriseOptions}
          onSearch={setEnterpriseSearchTerm}
          onFocus={resetSearchOnInteraction ? clearSearchTerm : undefined}
          onBlur={resetSearchOnInteraction ? clearSearchTerm : undefined}
          onChange={clearSiteOnChange || resetSearchOnInteraction ? handleEnterpriseChange : undefined}
          filterOption={false}
        />
      </DefaultFormItem>
    </FormItemCol>
  );
}

export default memo(BackOfficeEnterpriseSelect);
